/**
 * CR Debug: Site Debug Footer
 * 
 * Shows live site state for debugging layout/snapshot issues.
 * Only renders in development or when ?debug=1 is present.
 * 
 * CR150: Now accepts layout debug info from usePublicLayout hook
 * to show complete debugging information.
 * 
 * CR204: Added renderedBlocks prop to show which blocks were actually rendered.
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Bug, RefreshCw, AlertTriangle, CheckCircle, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DealCanonicalDebugPanel } from "./DealCanonicalDebugPanel";
import type { PublicLayoutDebugInfo } from "@/hooks/usePublicLayout";
import type { LayoutBlockId } from "@/lib/layout-studio/types";
import type { Deal } from "@/lib/types/deals";

/**
 * CR204: Block render result for debugging
 */
interface BlockRenderResult {
  blockId: LayoutBlockId;
  rendered: boolean;
  reason?: string;
}

interface SiteStateDebug {
  active_snapshot_id: string | null;
  active_batch_run_id: string | null;
  published_at: string | null;
  published_by: string | null;
  published_deal_count: number | null;
}

interface LayoutActivation {
  active_preset_id: string | null;
  updated_at: string | null;
  preset_name?: string | null;
}

interface DebugInfo {
  siteState: SiteStateDebug | null;
  layoutActivation: LayoutActivation | null;
  fetchedAt: string;
  buildVersion: string;
  error: string | null;
}

// Build version from environment (or default)
const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || "dev";

interface SiteDebugFooterProps {
  layoutDebugInfo?: PublicLayoutDebugInfo | null;
  /** CR204: Which blocks were actually rendered by LayoutRenderer */
  renderedBlocks?: BlockRenderResult[];
  /** CR207: Deals for canonical field diagnostics */
  deals?: Deal[];
}

export function SiteDebugFooter({ layoutDebugInfo, renderedBlocks = [], deals = [] }: SiteDebugFooterProps) {
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Check if debug mode is enabled
  const isDev = import.meta.env.DEV;
  const hasDebugParam = typeof window !== "undefined" && 
    new URLSearchParams(window.location.search).get("debug") === "1";
  
  const showDebug = isDev || hasDebugParam;
  
  // CR207: Canonical debug panel visible flag
  const showCanonicalDebug = hasDebugParam;

  const fetchDebugInfo = async () => {
    setLoading(true);
    try {
      // Fetch site_state
      const { data: siteState, error: stateError } = await supabase
        .from("site_state")
        .select("active_snapshot_id, active_batch_run_id, published_at, published_by, published_deal_count")
        .eq("key", "public")
        .single();

      // Fetch layout activation for 'publish' environment
      const { data: layoutActivation, error: layoutError } = await supabase
        .from("deal_finder_layout_activations")
        .select("active_preset_id, updated_at")
        .eq("environment", "publish")
        .single();

      // If we have a preset ID, fetch the preset name
      let presetName: string | null = null;
      if (layoutActivation?.active_preset_id) {
        const { data: preset } = await supabase
          .from("deal_finder_layout_presets")
          .select("name")
          .eq("id", layoutActivation.active_preset_id)
          .single();
        presetName = preset?.name || null;
      }

      const error = stateError?.message || layoutError?.message || null;

      setDebugInfo({
        siteState: siteState || null,
        layoutActivation: layoutActivation ? {
          ...layoutActivation,
          preset_name: presetName,
        } : null,
        fetchedAt: new Date().toISOString(),
        buildVersion: BUILD_VERSION,
        error,
      });
    } catch (err) {
      setDebugInfo({
        siteState: null,
        layoutActivation: null,
        fetchedAt: new Date().toISOString(),
        buildVersion: BUILD_VERSION,
        error: String(err),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showDebug) {
      fetchDebugInfo();
    }
  }, [showDebug]);

  if (!showDebug) {
    return null;
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const shortId = (id: string | null) => {
    if (!id) return "—";
    return id.length > 16 ? `${id.slice(0, 16)}…` : id;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg">
      {/* Collapsed bar */}
      <div 
        className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Bug className="h-3 w-3 text-orange-500" />
          <span className="font-semibold text-foreground">Debug</span>
          {debugInfo?.siteState && (
            <>
              <span className="mx-1">|</span>
              <span>Snapshot:</span>
              <span className="text-primary font-semibold">
                {shortId(debugInfo.siteState.active_snapshot_id)}
              </span>
              <span className="mx-1">|</span>
              <span>Layout:</span>
              <span className="text-primary font-semibold">
                {debugInfo.layoutActivation?.preset_name || shortId(debugInfo.layoutActivation?.active_preset_id)}
              </span>
              {/* CR151: Show detailsMode in collapsed bar */}
              {layoutDebugInfo?.detailsMode && (
                <>
                  <span className="mx-1">|</span>
                  <span>Details:</span>
                  <span className="text-green-600 font-semibold">
                    {layoutDebugInfo.detailsMode}
                  </span>
                </>
              )}
              <span className="mx-1">|</span>
              <span>Published:</span>
              <span className="text-foreground">
                {formatDate(debugInfo.siteState.published_at)}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-2"
            onClick={(e) => {
              e.stopPropagation();
              fetchDebugInfo();
            }}
            disabled={loading}
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <span className="text-[10px] text-muted-foreground">
            {expanded ? "▼" : "▲"}
          </span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && debugInfo && (
        <div className="px-4 py-3 border-t border-border bg-muted/30 text-xs font-mono space-y-3">
          {/* Site State */}
          <div>
            <h4 className="font-semibold text-foreground mb-1">Site State (source of truth for Live)</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
              <div>active_snapshot_id:</div>
              <div className="text-foreground break-all">{debugInfo.siteState?.active_snapshot_id || "—"}</div>
              <div>active_batch_run_id:</div>
              <div className="text-foreground break-all">{debugInfo.siteState?.active_batch_run_id || "—"}</div>
              <div>published_at:</div>
              <div className="text-foreground">{formatDate(debugInfo.siteState?.published_at)}</div>
              <div>published_by:</div>
              <div className="text-foreground">{debugInfo.siteState?.published_by || "—"}</div>
              <div>published_deal_count:</div>
              <div className="text-foreground">{debugInfo.siteState?.published_deal_count ?? "—"}</div>
            </div>
          </div>

          {/* Layout Activation (from DB query) */}
          <div>
            <h4 className="font-semibold text-foreground mb-1">Layout Activation (publish env - DB)</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
              <div>active_preset_id:</div>
              <div className="text-foreground break-all">{debugInfo.layoutActivation?.active_preset_id || "—"}</div>
              <div>preset_name:</div>
              <div className="text-primary font-semibold">{debugInfo.layoutActivation?.preset_name || "—"}</div>
              <div>updated_at:</div>
              <div className="text-foreground">{formatDate(debugInfo.layoutActivation?.updated_at)}</div>
            </div>
          </div>

          {/* CR285: Layout Version Stamp - prominent display for verification */}
          {layoutDebugInfo && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
              <h4 className="font-semibold text-blue-600 mb-2 flex items-center gap-2">
                📐 CR285 Layout Version Stamp
              </h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm font-mono">
                <span className="text-muted-foreground">env:</span>
                <span className="text-green-600 font-bold">publish</span>
                <span className="text-muted-foreground">preset_id:</span>
                <span className="text-foreground break-all">{layoutDebugInfo.activePresetId || "—"}</span>
                <span className="text-muted-foreground">preset_updated_at:</span>
                <span className="text-foreground">{formatDate(layoutDebugInfo.presetUpdatedAt)}</span>
                {layoutDebugInfo.headerBlock && (
                  <>
                    <span className="text-muted-foreground">header_title:</span>
                    <span className="text-primary font-bold">{layoutDebugInfo.headerBlock.titleText || "(default)"}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Layout Debug Info from usePublicLayout hook */}
          {layoutDebugInfo && (
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                  Layout Applied (usePublicLayout)
                  {layoutDebugInfo.usingFallback ? (
                    <span className="text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Fallback
                    </span>
                  ) : (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Active
                    </span>
                  )}
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
                  <div>environment:</div>
                  <div className="text-foreground">{layoutDebugInfo.environment}</div>
                  <div>activation_found:</div>
                  <div className={layoutDebugInfo.activationFound ? "text-green-600" : "text-amber-600"}>
                    {layoutDebugInfo.activationFound ? "Yes" : "No"}
                  </div>
                  <div>active_preset_id:</div>
                  <div className="text-foreground break-all">{layoutDebugInfo.activePresetId || "—"}</div>
                  <div>preset_name:</div>
                  <div className="text-primary font-semibold">{layoutDebugInfo.presetName || "—"}</div>
                  <div>preset_updated_at:</div>
                  <div className="text-foreground">{formatDate(layoutDebugInfo.presetUpdatedAt)}</div>
                  {/* CR151: Show detailsMode and source */}
                  <div>detailsMode:</div>
                  <div className="text-green-600 font-semibold">{layoutDebugInfo.detailsMode || "—"}</div>
                  <div>detailsMode source:</div>
                  <div className="text-foreground">{layoutDebugInfo.detailsModeSource || "—"}</div>
                  <div>using_fallback:</div>
                  <div className={layoutDebugInfo.usingFallback ? "text-amber-600" : "text-green-600"}>
                    {layoutDebugInfo.usingFallback ? "Yes (DEFAULT_LAYOUT)" : "No"}
                  </div>
                  {layoutDebugInfo.error && (
                    <>
                      <div>error:</div>
                      <div className="text-destructive">{layoutDebugInfo.error}</div>
                    </>
                  )}
                </div>
              </div>

              {/* CR201: Header Block Details */}
              {layoutDebugInfo.headerBlock && (
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Header Block (Page Branding)</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
                    <div>enabled:</div>
                    <div className={layoutDebugInfo.headerBlock.enabled ? "text-green-600" : "text-amber-600"}>
                      {layoutDebugInfo.headerBlock.enabled ? "Yes" : "No"}
                    </div>
                    <div>showLogo:</div>
                    <div className="text-foreground">{layoutDebugInfo.headerBlock.showLogo ? "Yes" : "No"}</div>
                    <div>showTitle:</div>
                    <div className="text-foreground">{layoutDebugInfo.headerBlock.showTitle ? "Yes" : "No"}</div>
                    <div>showSubtitle:</div>
                    <div className="text-foreground">{layoutDebugInfo.headerBlock.showSubtitle ? "Yes" : "No"}</div>
                    <div>titleText:</div>
                    <div className="text-primary font-semibold">{layoutDebugInfo.headerBlock.titleText || "—"}</div>
                    <div>subtitleText:</div>
                    <div className="text-foreground">{layoutDebugInfo.headerBlock.subtitleText || "—"}</div>
                    <div>logoAssetId:</div>
                    <div className="text-foreground break-all">{layoutDebugInfo.headerBlock.logoAssetId || "—"}</div>
                    
                    {/* CR202: Header validation warnings in debug */}
                    {layoutDebugInfo.headerBlock.enabled && (
                      <>
                        <div>header_empty:</div>
                        <div className={
                          !layoutDebugInfo.headerBlock.showLogo && 
                          !layoutDebugInfo.headerBlock.showTitle && 
                          !layoutDebugInfo.headerBlock.showSubtitle
                            ? "text-destructive font-semibold"
                            : "text-green-600"
                        }>
                          {!layoutDebugInfo.headerBlock.showLogo && 
                           !layoutDebugInfo.headerBlock.showTitle && 
                           !layoutDebugInfo.headerBlock.showSubtitle
                            ? "⚠️ Yes (no content visible)"
                            : "No"}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* CR204: Rendered Blocks Status */}
              {renderedBlocks.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Rendered Blocks (CR204 LayoutRenderer)
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {renderedBlocks.map((block) => (
                      <span
                        key={block.blockId}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                          block.rendered
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                        title={block.reason || (block.rendered ? "Rendered" : "Not rendered")}
                      >
                        {block.rendered ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <Circle className="w-3 h-3" />
                        )}
                        {block.blockId}
                        {block.reason && (
                          <span className="text-[10px] opacity-70">({block.reason})</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CR201: All Blocks from Layout Config */}
              {layoutDebugInfo.blocks.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Layout Config Blocks</h4>
                  <div className="flex flex-wrap gap-2">
                    {layoutDebugInfo.blocks
                      .sort((a, b) => a.order - b.order)
                      .map((block) => (
                        <span
                          key={block.id}
                          className={`px-2 py-0.5 rounded text-xs ${
                            block.enabled
                              ? "bg-primary/10 text-primary"
                              : "bg-muted text-muted-foreground line-through"
                          }`}
                        >
                          {block.id} ({block.order})
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Meta */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-muted-foreground">
              <span>Build: </span>
              <span className="text-foreground">{debugInfo.buildVersion}</span>
              <span className="mx-2">|</span>
              <span>DB Fetched: </span>
              <span className="text-foreground">{formatDate(debugInfo.fetchedAt)}</span>
              {layoutDebugInfo && (
                <>
                  <span className="mx-2">|</span>
                  <span>Layout Fetched: </span>
                  <span className="text-foreground">{formatDate(layoutDebugInfo.fetchedAt)}</span>
                </>
              )}
            </div>
            {debugInfo.error && (
              <div className="text-destructive">Error: {debugInfo.error}</div>
            )}
          </div>

          {/* Cache buster hint */}
          <div className="text-[10px] text-muted-foreground pt-1 border-t border-border">
            💡 <strong>Cache checks:</strong> Hard refresh (Cmd+Shift+R), Incognito, or add <code className="bg-muted px-1 rounded">?v={Date.now()}</code> to URL
          </div>

          {/* CR207: Canonical Field Diagnostics */}
          {showCanonicalDebug && deals.length > 0 && (
            <DealCanonicalDebugPanel deals={deals} className="mt-3" />
          )}
        </div>
      )}
    </div>
  );
}
